import _ from 'lodash';
import React from 'react';
import styled from 'styled-components';
import { useChangeAwareEffect } from 'use-change-aware-effect';
import { Link } from '../../shared/styles';
import {
  Button,
  Container,
  Row,
  Separator,
  SuccessMessage,
} from '../../common/form';
import { propTypes, defaultProps } from '../prop-types';
import {
  createFund,
  updateFund,
  setMemberRole,
  unsetMembersRoles,
  usePostQanda,
  useGetFundNetAssetsFromDataProvider,
  useGetFundFeesFromDataProvider,
  useGetFundPerformanceFromDataProvider,
} from '../../../../services/lwadmin/fund/fund-service';
import useGetQandasByFund from '../../../../services/funds/qanda-service';
import { useGetCompanies } from '../../../../services/lwadmin/company/company-service';
import useGetFundAssetClasses from '../../../../services/lwadmin/fund-asset-class/fund-asset-class-service';
import useGetStockCodes from '../../../../services/lwadmin/stock-code/stock-code-service';
import { getAllSeries } from '../../../../services/lwadmin/series/series-service';
import FundContext, { reducer, buildDefaultState } from './context';
import Partnership from './partnership';
import BasicFields from './basic-fields';
import FundFacts from './fund-facts';
import FundNetAssets from './fund-net-assets';
import DpFundNetAssets from './dp-fund-net-assets';
import FundPerformance from './fund-performance';
import DpFundPerformance from './dp-fund-performance';
import FundFees from './fund-fees';
import DpFundFees from './dp-fund-fees';
import FilesAndLinks from './files-and-links';
import FeaturedWires from './featured-wires';
import Qanda from './qanda';
import DataReviews from './data-reviews';
import ManualOverrides from './manual-overrides';
import Errors from './errors';
import withNameTextarea from './hoc-textarea';
import { performanceValidationErrors } from './performance-value';

const SContainer = styled(Container)`
  padding-bottom: 40px;
`;

const ButtonRow = styled(Row)`
  justify-content: flex-end;
`;

const SButton = styled(Button)`
  margin-left: 16px;
`;

const Disclaimer = withNameTextarea('disclaimer');

export default function Form({ fund, questions, autoSyncData }) {
  const {
    id,
    publicUrl,
    dataReviews,
  } = fund;
  const [state, dispatch] = React.useReducer(reducer, buildDefaultState(fund));
  const [showSuccessMessage, setShowSuccessMessage] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [gettingPerformance, setGettingPerformance] = React.useState(false);
  const [gettingFees, setGettingFees] = React.useState(false);
  const [gettingNetAssets, setGettingNetAssets] = React.useState(false);

  const [, getCompanies] = useGetCompanies();
  const [, getFundAssetClasses] = useGetFundAssetClasses();
  const [, getStockCodes] = useGetStockCodes();
  const [, getQandas] = useGetQandasByFund(false);
  const [, createQanda] = usePostQanda();
  const [, getPerformanceFromDataProvider] = useGetFundPerformanceFromDataProvider(state.fund.id);
  const [, getFeesFromDataProvider] = useGetFundFeesFromDataProvider(state.fund.id);
  const [, getNetAssetsFromDataProvider] = useGetFundNetAssetsFromDataProvider(state.fund.id);

  const getPerformance = e => {
    e && e.preventDefault();

    setGettingPerformance(true);

    getPerformanceFromDataProvider().then(resp => {
      setGettingPerformance(false);
      dispatch({ type: 'setDpPerformance', payload: resp.data });
    });
  };

  const getFees = e => {
    e && e.preventDefault();

    setGettingFees(true);

    getFeesFromDataProvider().then(resp => {
      setGettingFees(false);
      dispatch({ type: 'setDpFees', payload: resp.data });
    });
  };

  const getNetAssets = e => {
    e && e.preventDefault();

    setGettingNetAssets(true);

    getNetAssetsFromDataProvider().then(resp => {
      setGettingNetAssets(false);
      dispatch({ type: 'setDpNetAssets', payload: resp.data });
    });
  };

  const saveFund = e => {
    e && e.preventDefault();

    const f = state.fund;
    const fType = _.get(f, 'fundType.value');
    const payload = {
      name: f.name,
      fund_type: fType,
      apir_code: f.apirCode,
      data_provider_id: f.dataProviderId,
      isin: f.isin,
      stock_code: _.get(f, 'stockCode.value'),
      inception_date: f.inceptionDate,
      objective: f.objective,
      geographical_focus: f.geographicalFocus,
      investment_horizon: _.get(f, 'investmentHorizon.value'),
      minimum_investment: f.minimumInvestment,
      fund_size: f.fundSize,
      fund_size_date: f.fundSizeDate,
      currency: f.currency,
      legal_structure: f.legalStructure,
      domicile: f.domicile,
      invest_url: f.investUrl,
      fund_page_url: f.fundPageUrl,
      pds_file_url: !f.pdsFileUrl && autoSyncData ? f.dpPdsFileUrl : f.pdsFileUrl,
      monthly_update_file_url: f.monthlyUpdateFileUrl,
      tmd_file_url: f.tmdFileUrl,
      monthly_update_url_type: _.get(f, 'monthlyUpdateUrlType.value'),
      disclaimer: f.disclaimer,
      company_id: _.get(f, 'company.value'),
      fund_asset_class_id: _.get(f, 'fundAssetClass.value'),
      member_ids: (f.members || []).map(member => member.value),
      featured_wire_urls: _.compact(f.featuredWireUrls),
      performance: f.performance,
      management_fee: f.managementFee,
      performance_fee_charged: _.get(f, 'performanceFeeCharged.value'),
      performance_fee_pds: f.performanceFeePds,
      performance_fee: f.performanceFee,
      performance_fee_methodology: f.performanceFeeMethodology,
      total_cost_ratio: f.totalCostRatio,
      financial_year_end_fees_date: f.financialYearEndFeesDate,
      buy_spread: f.buySpread,
      sell_spread: f.sellSpread,
      pds_fees_date: f.pdsFeesDate,
      latest_prospectus_date: f.latestProspectusDate,
      day_end_entry_price: f.dayEndEntryPrice,
      day_end_exit_price: f.dayEndExitPrice,
      day_end_nav_price: f.dayEndNavPrice,
      day_end_pricing_update_date: f.dayEndPricingUpdateDate,
      day_end_market_price: f.dayEndMarketPrice,
      day_end_market_price_date: f.dayEndMarketPriceDate,
      day_end_nav: f.dayEndNav,
      day_end_nav_date: f.dayEndNavDate,
      fund_investor_vehicle_type: f.fundInvestorVehicleType,
      dp_category: f.dpFundCategory,
      wholesale: f.wholesale,
      series_ids: (f.series || []).map(s => s.value),
    };

    const performanceErrors = performanceValidationErrors(f.performance);
    if (performanceErrors) {
      setShowSuccessMessage(false);
      dispatch({ type: 'setErrors', payload: performanceErrors });
      return;
    }

    setSaving(true);
    const createOrUpdate = f.id ? updateFund(f.id, payload) : createFund(payload);

    createOrUpdate.then(async resp => {
      const createdId = _.get(resp, 'data.id');
      const updatedIsin = _.get(resp, 'data.isin') || '';
      const updatedDpPdsFileUrl = _.get(resp, 'data.dpPdsFileUrl') || '';
      if (_.get(state, 'fund.isin') !== updatedIsin) {
        dispatch({ type: 'setIsin', payload: updatedIsin });
      }
      if (_.get(state, 'fund.dpPdsFileUrl') !== updatedDpPdsFileUrl) {
        dispatch({ type: 'setDpPdsFileUrl', payload: updatedDpPdsFileUrl });
      }

      if (state.manager) {
        await setMemberRole(f.id || createdId, state.manager.value, 'manager');
      } else if (f.id) {
        unsetMembersRoles(f.id);
      }

      if (!f.id) {
        dispatch({ type: 'setId', payload: createdId });
        const answered = state.qandas.filter(
          // no type no Q&A
          q => fType && _.indexOf(q.fundTypes, fType) >= 0 && q.answer !== null,
        );
        if (answered.length) {
          const qandas = await Promise.all(answered.map(qanda => createQanda(createdId, qanda)));
          dispatch({
            type: 'setQandaIds',
            payload: answered.map((d, i) => ({ id: qandas[i].id, questionId: d.questionId })),
          });
        }
      }
      setSaving(false);
      setShowSuccessMessage(true);
      dispatch({ type: 'setErrors', payload: {} });
    }).catch(error => {
      setSaving(false);
      setShowSuccessMessage(false);
      dispatch({ type: 'setErrors', payload: _.get(error, 'response.data', {}) });
    });
  };

  useChangeAwareEffect(({ isMount }) => {
    if (!isMount) {
      return;
    }
    getCompanies().then(({ data }) => {
      data && dispatch({ type: 'setCompanyOptions', payload: data });
    });
    getFundAssetClasses().then(({ data }) => {
      data && dispatch({ type: 'setFundAssetClassOptions', payload: data });
    });
    getStockCodes().then(({ data }) => {
      data && dispatch({ type: 'setStockCodeOptions', payload: data });
    });
    getAllSeries('active').then(({ data }) => {
      data && dispatch({ type: 'setSeriesOptions', payload: data });
    });
    if (id) {
      getQandas(id).then(({ data }) => {
        data && dispatch({ type: 'setQandas', payload: { questions, answers: data } });
      });
    } else {
      dispatch({ type: 'setQandas', payload: { questions, answers: null } });
    }

    if (autoSyncData) {
      getPerformance();
      getFees();
      getNetAssets();
      saveFund();
      dispatch({ type: 'setPdsfileurl', payload: state.fund.dpPdsFileUrl });
    }
  }, {});

  React.useEffect(() => {
    setShowSuccessMessage(false);
    dispatch({ type: 'setErrors', payload: {} });
  }, [state.fund]);

  const getLatestPDS = e => {
    e && e.preventDefault();

    dispatch({ type: 'setPdsfileurl', payload: state.fund.dpPdsFileUrl });

    window.open(state.fund.dpPdsFileUrl, '_blank', 'noreferrer');
  };

  const partnership = _.get(state, 'fund.company.partnership');

  return (
    <FundContext.Provider value={{ state, dispatch }}>
      <SContainer>
        <Link href={publicUrl} target="_blank">View</Link>
        <Partnership partnership={partnership} />
        <div>
          <BasicFields />
          <Separator />
          <FundFacts />
          <Separator />
          <FundNetAssets />
          <Separator />
          <DpFundNetAssets />
          <Separator />
          <FundPerformance />
          <Separator />
          <DpFundPerformance />
          <Separator />
          <FundFees />
          <Separator />
          <DpFundFees />
          <Separator />
          <FilesAndLinks />
          <Separator />
          <FeaturedWires />
          <Separator />
          <Qanda />
          <Separator />
          <ManualOverrides />
          {dataReviews.length > 0 && (
            <>
              <Separator />
              <DataReviews />
            </>
          )}
        </div>
        <Separator />
        <Disclaimer />
        <Errors />
        {showSuccessMessage && <SuccessMessage>Fund successfully saved</SuccessMessage>}
        <ButtonRow>
          <SButton className="btn btn-default" onClick={getLatestPDS}>
            Get Latest PDS
          </SButton>
          <SButton className="btn btn-default" onClick={getNetAssets} disabled={gettingNetAssets}>
            { gettingNetAssets ? 'Getting Net Assets' : 'Get Net Assets' }
          </SButton>
          <SButton className="btn btn-default" onClick={getFees} disabled={gettingFees}>
            { gettingFees ? 'Getting Fees' : 'Get Fees' }
          </SButton>
          <SButton className="btn btn-default" onClick={getPerformance} disabled={gettingPerformance}>
            { gettingPerformance ? 'Getting Performance' : 'Get Performance' }
          </SButton>
          <SButton className="btn btn-primary" onClick={saveFund} disabled={saving}>
            {saving ? 'Saving' : 'Save'}
          </SButton>
        </ButtonRow>
      </SContainer>
    </FundContext.Provider>
  );
}

Form.propTypes = propTypes;
Form.defaultProps = defaultProps;
